import React, { useEffect } from 'react';
import {useLocation} from "react-router-dom";
import { useForm, Controller } from 'react-hook-form';
import ReactModal from 'react-modal';
import Select from 'react-select';
import Dropzone from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faFile } from '@fortawesome/free-solid-svg-icons';
import ReactTooltip from 'react-tooltip';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import { tasklistApi } from '../../../apis/tasklist/tasklistApi';
import { SelectLabelModal } from './SelectLabelModal';
import { roomattachfileApi } from '../../../apis/roomattachfile/roomattachfileApi';
import { getLabelColor } from '../../../libs/utils/getLabelColor';
import { LabelList } from '../../../types/types';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface Provided {
  [key: string]: string;
}

const exclusionFileType: string[] = [
  'application/octet-stream',
  'application/x-msdownload',
];

const customStyles = {
  control: (provided: Provided) => ({
    ...provided,
    backgroundColor: '#6280a2',
    color: 'white',
    minHeight: 'auto',
    height: '1.75rem',
  }),
  placeholder: () => ({
    height: '100%',
    color: 'white',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
  }),
  indicatorsContainer: (provided: Provided) => ({
    ...provided,
    height: '100%',
  }),
  indicatorSeparator: () => ({
    display: 'none',
    height: '100%',
  }),
  singleValue: () => ({
    height: '100%',
    color: 'white',
    fontSize: '0.75rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline',
    paddingTop: 4,
    paddingBottom: 4,
  }),
  input: () => ({
    height: '100%',
    width: '100%',
    overflow: 'scroll',
    color: 'white',
    fontSize: '0.75rem',
    marginTop: 0,
    marginBottom: 0,
    display: 'flex',
    alignItems: 'center',
  }),
  valueContainer: (provided: Provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    height: '100%',
    width: '80%',
    overflow: 'visible',
  }),
  menu: (provided: Provided) => ({
    ...provided,
    marginTop: 2,
  }),
  option: (provided: Provided) => ({
    ...provided,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

interface CreateRoomAttachFileModalProps {
  onSubmitted: () => Promise<void>;
}

interface FormValues {
  taskName: string;
  list: SelectOptions;
}

interface SelectOptions {
  value: number;
  label: string;
}

export const CreateRoomAttachFileModal = (
  props: CreateRoomAttachFileModalProps
) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const { onSubmitted } = props;
  const { success, danger } = useSystemMessage();
  const [lists, setLists] = React.useState<SelectOptions[]>([]);
  const [labels, setLabels] = React.useState<LabelList[]>([]);
  const [showLabelModal, setShowLabelModal] = React.useState<boolean>(false);
  const [file, setFile] = React.useState<File | undefined>();
  const location = useLocation();
  const { goto } = useAppRouter();

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await tasklistApi.index(false, {});
        setLists(
          response.listList.map((l) => ({ value: l.id, label: l.name }))
        );
      } catch(e) {
        await logError(
          location.pathname,
          'タスク一覧の読み込みに失敗しました。',
          e
        );

        goto('internalServerError');
      }
    };
    fetch();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const encode = async (data: File): Promise<string | undefined> => {
    return new Promise<string | undefined>((resolve) => {
      const reader = new FileReader();

      reader.onload = () => {
        const dataURI = reader.result;

        if (dataURI === null || dataURI instanceof ArrayBuffer) {
          return;
        }
        resolve(dataURI);
      };
      reader.readAsDataURL(data);
    });
  };

  const onSubmit = async (data: FormValues) => {
    if (file === undefined) {
      await danger('ファイルが選択されていません');
      return;
    }

    const base64EncodedFile = await encode(file);

    if (base64EncodedFile === undefined) {
      await danger('ファイルが選択されていません');
      return;
    }

    try {
      await roomattachfileApi.create(
        {
          listId: data.list.value,
          label: labels.map((l) => l.id),
          taskName: data.taskName,
          fileLevel: 1,
          originalFileName: file.name,
          attachment: base64EncodedFile,
        },
        {}
      );
      await success('ファイルを追加しました');
      await onSubmitted();
    } catch(e) {
      await logError(
        location.pathname,
        'タスクファイルの追加に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const handleDrop = async (acceptedFile: File) => {
    // exclusionFileTypeのファイルタイプは保存できないようにする
    // .dmgファイルはtypeがblankになる
    if (
      exclusionFileType.includes(acceptedFile.type) ||
      acceptedFile.type === ''
    ) {
      await danger('この種類のファイルのアップロードはできません。');
      return;
    }

    // 5MB以上のファイルは保存できないようにする
    if (acceptedFile.size >= 5 * 1024 * 1024) {
      await danger('保存可能なファイルサイズは5MBまでです。');
      return;
    }

    setFile(acceptedFile);
    setValue('file', acceptedFile);
    clearErrors('file');
  };

  const handleClear = () => {
    setFile(undefined);
    setValue('file', undefined);
  };

  return (
    <>
      {showLabelModal && (
        <SelectLabelModal {...{ labels, setLabels, setShowLabelModal }} />
      )}
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted()}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">
              タスクを新規作成してファイルを追加
            </h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted()}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <div className="px-2 mb-4">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register('taskName', { required: true, maxLength: 100 })}
                  type="text"
                  placeholder="新規タスクを入力してください"
                  className="text-field"
                />
                {errors.taskName && errors.taskName.type === 'required' && (
                  <span className="my-2 text-red-500 text-xs">
                    タスク名は必須です
                  </span>
                )}
                {errors.taskName && errors.taskName.type === 'maxLength' && (
                  <span className="my-2 text-red-500 text-xs">
                    タスク名は100文字以下で指定してください
                  </span>
                )}
              </div>
              <div className="px-2 mb-4 flex items-center w-full">
                <div className="flex-auto w-8/12">
                  <Controller
                    control={control}
                    name="list"
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        placeholder="リストを選択"
                        options={lists}
                        value={value}
                        styles={customStyles}
                        onChange={onChange}
                      />
                    )}
                  />
                  {errors.list && (
                    <span className="my-2 text-red-500 text-xs">
                      リスト名は必須です
                    </span>
                  )}
                </div>
                <div className="h-7 w-32 ml-8 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowLabelModal(true)}
                    className="h-full w-full rounded-sm bg-primary text-white text-xs rounded-sm outline-none focus:outline-none"
                  >
                    ラベルを追加
                  </button>
                </div>
              </div>
              <div className="px-2 flex flex-wrap items-center w-full">
                {labels.length !== 0 && (
                  <p className="text-xs text-primary w-full mb-2">
                    選択中のラベル
                  </p>
                )}
                {labels.map((l) => (
                  <>
                    <span
                      key={`label-${l.id}`}
                      className={`label-item-6 mb-2 ${getLabelColor(l.color)}`}
                      data-tip={l.name}
                      data-for={`label-${l.id}`}
                    >
                      {l.name}
                    </span>
                    <ReactTooltip
                      place="top"
                      type="dark"
                      effect="solid"
                      id={`label-${l.id}`}
                      className="max-w-xs break-all text-xs"
                    />
                  </>
                ))}
              </div>
              <div className="border-t border-primary my-4" />
              <section className="px-2">
                <input
                  type="hidden"
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register('file', { required: true })}
                />
                <Dropzone
                  multiple={false}
                  onDrop={(acceptedFiles) => handleDrop(acceptedFiles[0])}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...getRootProps({
                        className:
                          'h-48 bg-white flex items-center justify-center cursor-pointer',
                      })}
                    >
                      {file === undefined ? (
                        <>
                          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                          <input {...getInputProps()} />
                          <p className="text-sm text-primary font-semibold">
                            ファイルをドラッグ＆ドロップしてください
                          </p>
                        </>
                      ) : (
                        <div className="flex flex-col justify-center w-36">
                          <div className="thumbnail-container">
                            {file.type.includes('image') ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt="thumbnail"
                                className="thumbnail"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <FontAwesomeIcon
                                  icon={faFile}
                                  size="2x"
                                  className="text-gray-500"
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex justify-center w-full">
                            <p className="text-xs text-primary font-semibold truncate">
                              {file.name}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="text-xs my-2 outline-none focus:outline-none"
                            onClick={handleClear}
                          >
                            ファイルをクリア
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </Dropzone>

                <span className="my-2 text-primary text-xs font-semibold">
                  ※ アップロードできるファイルのサイズは5MBまでです
                </span>
                {errors.file && (
                  <span className="my-2 text-red-500 text-xs">
                    ファイルは必須です
                  </span>
                )}
              </section>
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => onSubmitted()}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      保 存
                    </>
                  ) : (
                    '保　存'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
